/** Key material is carried in the URL fragment, not in requests to the server. */
const KEY_NAME = 'e2ee';

function checkedUrl(value: string): URL {
  const url = new URL(value);
  if (url.protocol !== 'https:' || url.hostname !== 'app.vorbello.eu' || url.username || url.password || (url.port && url.port !== '443')) {
    throw new Error('Adresa invitației criptate nu este validă.');
  }
  const id = url.searchParams.get('meeting') || url.searchParams.get('room') || '';
  if (!/^(?:meet|room)-[a-f0-9]{32}$/.test(id) || (url.searchParams.has('meeting') && url.searchParams.has('room'))) {
    throw new Error('Identificatorul invitației criptate nu este valid.');
  }
  return url;
}

export function createE2eeKey(source: Crypto = globalThis.crypto): Uint8Array {
  if (!source?.getRandomValues) throw new Error('Generatorul securizat nu este disponibil.');
  return source.getRandomValues(new Uint8Array(32));
}

/** Domain-separated room binding for experimental tests; never exposes the key. */
export async function deriveE2eeRoomBinding(eventId: string, key: Uint8Array, source: Crypto = globalThis.crypto): Promise<string> {
  if (!/^(?:meet|room)-[a-f0-9]{32}$/.test(eventId) || !(key instanceof Uint8Array) || key.byteLength !== 32 || !source?.subtle) {
    throw new Error('Nu se poate identifica sesiunea criptată.');
  }
  const prefix = new TextEncoder().encode(`vorbello-e2ee-room-v1\0${eventId}\0`);
  const material = new Uint8Array(prefix.byteLength + key.byteLength);
  material.set(prefix); material.set(key, prefix.byteLength);
  try {
    const digest = new Uint8Array(await source.subtle.digest('SHA-256', material));
    return Array.from(digest, b => b.toString(16).padStart(2, '0')).join('');
  } finally {
    material.fill(0);
  }
}

export function attachE2eeKey(invitation: string, key: Uint8Array): string {
  const url = checkedUrl(invitation);
  if (!(key instanceof Uint8Array) || key.byteLength !== 32) throw new Error('Cheia criptării este invalidă.');
  const params = new URLSearchParams(url.hash.slice(1));
  if (params.has(KEY_NAME)) throw new Error('Invitația conține deja o cheie.');
  const token = btoa(Array.from(key, byte => String.fromCharCode(byte)).join('')).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  params.set(KEY_NAME, token);
  url.hash = params.toString();
  return url.toString();
}

export function extractE2eeKey(invitation: string): { cleanUrl: string; key: Uint8Array | null } {
  const url = checkedUrl(invitation), params = new URLSearchParams(url.hash.slice(1));
  const values = params.getAll(KEY_NAME);
  if (values.length > 1) throw new Error('Invitația conține mai multe chei.');
  if (!values.length) return { cleanUrl: url.toString(), key: null };
  if (!/^[A-Za-z0-9_-]{43}$/.test(values[0])) throw new Error('Cheia criptării este invalidă.');
  const raw = atob(values[0].replace(/-/g, '+').replace(/_/g, '/') + '=');
  const key = Uint8Array.from(raw, c => c.charCodeAt(0));
  if (key.length !== 32 || btoa(raw).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '') !== values[0]) throw new Error('Cheia criptării este invalidă.');
  params.delete(KEY_NAME);
  url.hash = params.toString();
  return { cleanUrl: url.toString(), key };
}
